/**
 * Pay Range Transparency State Requirements
 * 
 * Many US states now require employers to include salary/pay range information 
 * in job postings. This file defines which states require pay transparency
 * and provides utilities for checking requirements.
 */

export interface PayTransparencyState {
  stateShort: string;
  stateName: string;
  effectiveDate: string;
  employeeThreshold?: number;
  notes: string;
}

/**
 * List of US states that require pay range disclosure in job postings
 */
export const PAY_TRANSPARENCY_STATES: PayTransparencyState[] = [
  {
    stateShort: 'CO',
    stateName: 'Colorado',
    effectiveDate: '2021-01-01',
    notes: 'Equal Pay for Equal Work Act requires pay range in all job postings.',
  },
  {
    stateShort: 'CA',
    stateName: 'California',
    effectiveDate: '2023-01-01',
    notes: 'Employers must include a "pay scale" in job postings.',
  },
  {
    stateShort: 'NY',
    stateName: 'New York',
    effectiveDate: '2023-09-17',
    employeeThreshold: 4,
    notes: 'Employers with 4+ employees must include salary range in job ads.',
  },
  {
    stateShort: 'WA',
    stateName: 'Washington',
    effectiveDate: '2023-01-01',
    employeeThreshold: 15,
    notes: 'Employers with 15+ employees must include pay range in postings.',
  },
  {
    stateShort: 'HI',
    stateName: 'Hawaii',
    effectiveDate: '2024-01-01',
    employeeThreshold: 50,
    notes: 'Employers with 50+ employees must include hourly or salary range.',
  },
  {
    stateShort: 'IL',
    stateName: 'Illinois',
    effectiveDate: '2025-01-01',
    employeeThreshold: 15,
    notes: 'Employers with 15+ employees must provide pay scale and benefits description.',
  },
  {
    stateShort: 'MN',
    stateName: 'Minnesota',
    effectiveDate: '2025-01-01',
    employeeThreshold: 30,
    notes: 'Employers with 30+ employees must include starting salary range or fixed pay rate.',
  },
  {
    stateShort: 'NJ',
    stateName: 'New Jersey',
    effectiveDate: '2025-06-01',
    employeeThreshold: 10,
    notes: 'Employers with 10+ employees must include salary range and benefits description.',
  },
  {
    stateShort: 'MA',
    stateName: 'Massachusetts',
    effectiveDate: '2025-10-29',
    employeeThreshold: 25,
    notes: 'Employers with 25+ employees must include pay range in job advertisements.',
  },
  {
    stateShort: 'VT',
    stateName: 'Vermont',
    effectiveDate: '2025-07-01',
    employeeThreshold: 5,
    notes: 'Employers with 5+ employees must include salary range estimate in job postings.',
  },
  {
    stateShort: 'MD',
    stateName: 'Maryland',
    effectiveDate: '2024-10-01',
    notes: 'All employers must include wage range along with benefits/compensation.',
  },
];

/**
 * State short codes that require pay transparency
 */
export const PAY_TRANSPARENCY_STATE_CODES = PAY_TRANSPARENCY_STATES.map((s) => s.stateShort);

/**
 * Check if a state has pay transparency laws on the books.
 */
export function isPayRangeTransparencyState(stateShort: string | undefined | null): boolean {
  if (!stateShort) return false;
  return PAY_TRANSPARENCY_STATE_CODES.includes(stateShort.toUpperCase());
}

/**
 * Form validation — always optional for now.
 */
export function isPayRangeRequired(stateShort: string | undefined | null): boolean {
  void stateShort;
  return false;
}

/**
 * True when state law clearly applies (no employee threshold, or at/above threshold).
 */
export function isDefinitePayRangeViolation(
  stateShort: string | undefined | null,
  employeeCount?: number | null,
): boolean {
  const info = getPayTransparencyInfo(stateShort);
  if (!info) return false;
  if (info.employeeThreshold == null) return true;
  if (employeeCount == null) return false;
  return employeeCount >= info.employeeThreshold;
}

/**
 * Get the pay transparency info for a specific state
 */
export function getPayTransparencyInfo(stateShort: string | undefined | null): PayTransparencyState | undefined {
  if (!stateShort) return undefined;
  return PAY_TRANSPARENCY_STATES.find((s) => s.stateShort === stateShort.toUpperCase());
}

/**
 * Pay range type options for the dropdown
 */
export type PayRangeType = 'hourly' | 'annual' | 'commission' | 'project' | 'na';

export interface PayRangeOption {
  id: PayRangeType;
  name: string;
  description: string;
}

export const PAY_RANGE_TYPE_OPTIONS: PayRangeOption[] = [
  { id: 'hourly', name: 'Hourly Rate', description: 'Pay per hour worked' },
  { id: 'annual', name: 'Annual Salary', description: 'Yearly compensation' },
  { id: 'commission', name: 'Commission Based', description: 'Pay based on sales or performance' },
  { id: 'project', name: 'Per Project', description: 'Fixed pay per project' },
];

export const NA_PAY_RANGE_OPTION: PayRangeOption = {
  id: 'na',
  name: 'N/A',
  description: 'Pay range not applicable',
};

export function isSuiteRentalCategory(
  jobCategory?: { name?: string; alias?: string } | null,
): boolean {
  if (!jobCategory?.name && !jobCategory?.alias) {
    return false;
  }
  const name = (jobCategory.name || '').trim().toLowerCase();
  const alias = (jobCategory.alias || '').trim().toLowerCase();
  return name === 'suite rental' || alias === 'suite_rental';
}

export function isPayRangeNaAllowed(
  payRangeRequired: boolean,
  jobCategory?: { name?: string; alias?: string } | null,
): boolean {
  return !payRangeRequired || isSuiteRentalCategory(jobCategory);
}

export function getPayRangeTypeOptions(
  payRangeRequired: boolean,
  jobCategory?: { name?: string; alias?: string } | null,
): PayRangeOption[] {
  if (isPayRangeNaAllowed(payRangeRequired, jobCategory)) {
    return [...PAY_RANGE_TYPE_OPTIONS, NA_PAY_RANGE_OPTION];
  }
  return PAY_RANGE_TYPE_OPTIONS;
}

/**
 * Predefined pay range presets for quick selection
 */
export interface PayRangePreset {
  id: string;
  label: string;
  minValue: number;
  maxValue: number;
  type: PayRangeType;
}

export const HOURLY_RATE_PRESETS: PayRangePreset[] = [
  { id: 'hourly_15_20', label: '$15 - $20/hr', minValue: 15, maxValue: 20, type: 'hourly' },
  { id: 'hourly_20_25', label: '$20 - $25/hr', minValue: 20, maxValue: 25, type: 'hourly' },
  { id: 'hourly_25_35', label: '$25 - $35/hr', minValue: 25, maxValue: 35, type: 'hourly' },
  { id: 'hourly_35_50', label: '$35 - $50/hr', minValue: 35, maxValue: 50, type: 'hourly' },
  { id: 'hourly_50_75', label: '$50 - $75/hr', minValue: 50, maxValue: 75, type: 'hourly' },
  { id: 'hourly_75_100', label: '$75 - $100/hr', minValue: 75, maxValue: 100, type: 'hourly' },
  { id: 'hourly_100_plus', label: '$100+/hr', minValue: 100, maxValue: 150, type: 'hourly' },
];

export const ANNUAL_SALARY_PRESETS: PayRangePreset[] = [
  { id: 'annual_30_40', label: '$30,000 - $40,000', minValue: 30000, maxValue: 40000, type: 'annual' },
  { id: 'annual_40_50', label: '$40,000 - $50,000', minValue: 40000, maxValue: 50000, type: 'annual' },
  { id: 'annual_50_65', label: '$50,000 - $65,000', minValue: 50000, maxValue: 65000, type: 'annual' },
  { id: 'annual_65_80', label: '$65,000 - $80,000', minValue: 65000, maxValue: 80000, type: 'annual' },
  { id: 'annual_80_100', label: '$80,000 - $100,000', minValue: 80000, maxValue: 100000, type: 'annual' },
  { id: 'annual_100_125', label: '$100,000 - $125,000', minValue: 100000, maxValue: 125000, type: 'annual' },
  { id: 'annual_125_150', label: '$125,000 - $150,000', minValue: 125000, maxValue: 150000, type: 'annual' },
  { id: 'annual_150_plus', label: '$150,000+', minValue: 150000, maxValue: 200000, type: 'annual' },
];

export const PROJECT_RATE_PRESETS: PayRangePreset[] = [
  { id: 'project_100_500', label: '$100 - $500', minValue: 100, maxValue: 500, type: 'project' },
  { id: 'project_500_1000', label: '$500 - $1,000', minValue: 500, maxValue: 1000, type: 'project' },
  { id: 'project_1000_2500', label: '$1,000 - $2,500', minValue: 1000, maxValue: 2500, type: 'project' },
  { id: 'project_2500_5000', label: '$2,500 - $5,000', minValue: 2500, maxValue: 5000, type: 'project' },
  { id: 'project_5000_plus', label: '$5,000+', minValue: 5000, maxValue: 10000, type: 'project' },
];

/**
 * Format a pay range for display
 */
export function formatPayRange(
  minValue: number | undefined | null,
  maxValue: number | undefined | null,
  type: PayRangeType | undefined | null,
): string {
  if (type === 'na') {
    return 'N/A';
  }

  if (!minValue && !maxValue) {
    if (!type) {
      return '';
    }
    return PAY_RANGE_TYPE_OPTIONS.find((option) => option.id === type)?.name || '';
  }
  
  const formatCurrency = (val: number) => {
    if (val >= 1000) {
      return `$${val.toLocaleString()}`;
    }
    return `$${val}`;
  };

  const suffix = type === 'hourly' ? '/hr' : type === 'annual' ? '/year' : type === 'project' ? '/project' : '';
  
  if (minValue && maxValue) {
    return `${formatCurrency(minValue)} - ${formatCurrency(maxValue)}${suffix}`;
  } else if (minValue) {
    return `${formatCurrency(minValue)}+${suffix}`;
  } else if (maxValue) {
    return `Up to ${formatCurrency(maxValue)}${suffix}`;
  }
  
  return '';
}

/**
 * Get all presets for a given pay range type
 */
export function getPresetsForType(type: PayRangeType): PayRangePreset[] {
  switch (type) {
    case 'hourly':
      return HOURLY_RATE_PRESETS;
    case 'annual':
      return ANNUAL_SALARY_PRESETS;
    case 'project':
      return PROJECT_RATE_PRESETS;
    case 'commission':
      return []; // Commission-based doesn't have presets
    case 'na':
      return [];
    default:
      return [];
  }
}
